/**
 * Pick a stable React key from a list of candidate fields on an item.
 *
 * Returns the first candidate that is defined, non-null, and not an
 * empty string — coerced to string. Returns ``undefined`` if no
 * candidate yields a usable value, so React's own dev-time warning
 * surfaces the gap rather than silently colliding under index keys.
 *
 * Bug #16 (audit 2026-05-06): list rendering used either bare
 * ``key={index}`` or ``key={item.id ?? index}``. Index keys make
 * React associate a DOM node with a *position* in the list, which
 * breaks under reorders/sorts/filters: the same DOM node is reused
 * for a different item, which collides focus, animations, and any
 * external refs.
 *
 * Usage:
 *   <li key={pickStableKey(item, ["market_id", "condition_id", "id"])}>
 *
 * If you have a row that genuinely lacks any stable identifier, that
 * is an upstream API contract problem — fix it at the data layer
 * (or assign a generated UUID at fetch time) rather than papering
 * over it with the row's index.
 */
export function pickStableKey(
  item: Record<string, unknown>,
  candidates: readonly string[],
): string | undefined {
  for (const field of candidates) {
    const value = item[field];
    if (value === null || value === undefined) continue;
    const asString = String(value);
    if (asString.length === 0) continue;
    return asString;
  }
  return undefined;
}
